import React, { useState, useEffect, useMemo } from "react";
import { get, post, put, remove } from "../../shared/http/httpService";
import { useTranslation } from "react-i18next";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import EditModal from "./EditModal.component";
import { useSnackbar } from "notistack";

import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { MRT_Localization_ES } from "material-react-table/locales/es";
import { MRT_Localization_EN } from "material-react-table/locales/en";
import { DeleteRowModal } from "../../shared/helper/tableHelper";
import { Box, IconButton, Tooltip, Typography, Button, ThemeProvider } from "@mui/material";
import TwoFAModal from "./Two2faModal.component";

import {
  tableLightTheme,
  tableDarkTheme,
} from "../../shared/theming/table.theme";
import { useSelector } from "react-redux";

//Clonar el tema y eliminar la propiedad shadows
const modifiedLightTheme = { ...tableLightTheme, shadows: [] };
const modifiedDarkTheme = { ...tableDarkTheme, shadows: [] };

const TransferTable = ({ providersData, userPermissions }) => {
  const { t, i18n } = useTranslation("transfersTable");
  const { enqueueSnackbar } = useSnackbar();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editRow, seteditRow] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [typeModal, setTypeModal] = useState(null);
  const [clientList, setClientList] = useState([]);
  const [data, setData] = useState([]);
  const theme = useSelector((state) => state.theme);
  const [twoFAOpen, setTwoFAOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const columns = useMemo(
    () => [
      {
        header: t("inboundNameTransfer"),
        accessorKey: "inboundName",
      },
      {
        header: t("transferName"),
        accessorKey: "transferName",
        filterVariant: "multi-select",
        filterSelectOptions: clientList.map((x) => ({
          label: x.display_name,
          value: x.name,
        })),
        filterFn: (rows, id, filterValue) => {
          if (filterValue.length === 0) return true;
          return filterValue.includes(rows.original.transferName);
        },
        //change render value to displayName
        Cell: ({ cell }) => {
          const client = clientList.find((x) => x.name === cell.getValue());
          return client ? client.display_name : cell.getValue();
        },
      },
      {
        header: t("providerTransfer"),
        accessorKey: "provider",
        Cell: ({ cell }) => {
          return t(cell.getValue());
        },
      },
      {
        header: t("descriptionTransfer"),
        accessorKey: "description",
      },
    ],
    [clientList, i18n.language],
  );

  useEffect(() => {
    getTransferTable();
    getClientList();
  }, []);

  const getTransferTable = () => {
    get("/transferTable")
      .then((data) => {
        setData(data);
      })
      .catch(() => {
        enqueueSnackbar(t("errorGettingRoute"), {
          variant: "error",
        });
      });
  };

  const getClientList = () => {
    get("/essentials/data/clients")
      .then((data) => {
        data.sort((a, b) => a.display_name.localeCompare(b.display_name));
        setClientList(data);
      })
      .catch(() => {
        enqueueSnackbar(t("errorGettingRouteTransfer"), {
          variant: "error",
        });
      });
  };

  const newTransferTable = async (row, skip2FA = false) => {
    const body = {
      inboundName: row.inboundName,
      transferName: row.transferName,
      provider: row.provider,
      description: row.description,
    };

    try {
      if (!skip2FA) {
        const verifyResp = await post("/users/validate2fa");
        const requires2FA = verifyResp?.requires2FA;

        if (requires2FA) {
          setPendingAction(() => () => newTransferTable(row, true));
          setTwoFAOpen(true);
          return;
        }
      }

      await post("/transferTable", body);

      enqueueSnackbar(t("newSavedTransfer"), {
        variant: "success",
      });

      getTransferTable();
      setTypeModal(null);
    } catch (err) {
      const errorMsg = err?.message?.msg || err?.message || t("errorTransfer");
      enqueueSnackbar(errorMsg, {
        variant: "error",
      });
    }
  };

  const updateTransferTable = async (row, values, skip2FA = false) => {
    const body = {
      _id: row.original._id,
      inboundName: values.inboundName,
      transferName: values.transferName,
      provider: values.provider,
      description: values.description,
    };

    try {
      if (!skip2FA) {
        const verifyResp = await post("/users/validate2fa");
        const requires2FA = verifyResp?.requires2FA;

        if (requires2FA) {
          setPendingAction(() => () => updateTransferTable(row, values, true));
          setTwoFAOpen(true);
          return;
        }
      }

      await put("/transferTable", body);

      enqueueSnackbar(t("updatedTransfer"), { variant: "success" });

      getTransferTable();
      setTypeModal(null);
    } catch (err) {
      const errorMsg = err?.message?.msg || err?.message || t("errorTransfer");
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };

  const handleConfirm2FA = async (token) => {
    try {
      const resp = await post("/users/validate2fa", { token });

      if (resp.requires2FA === false) {
        setTwoFAOpen(false);
        setPendingAction(null);
        pendingAction();
        return;
      }

      const errorMsg = resp.msg || t("incorrectCode");
      enqueueSnackbar(errorMsg, { variant: "error" });
    } catch (err) {
      const errorMsg = err?.message?.msg || err?.message || t("errorVerifying2FA");
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };

  const handleResendToken = async () => {
    try {
      const resp = await post("/users/validate2fa");
      
      if (resp.requires2FA) {
        enqueueSnackbar(t("codeResent"), { variant: "success" });
      }
    } catch (err) {
      const errorMsg = err?.message?.msg || err?.message || t("errorResending2FA");
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };

  const deleteTransferTable = async (row, skip2FA = false) => {
    const body = {
      _id: row.original._id,
    };

    try {
      if (!skip2FA) {
        const verifyResp = await post("/users/validate2fa");
        const requires2FA = verifyResp?.requires2FA;

        if (requires2FA) {
          setPendingAction(() => () => deleteTransferTable(row, true));
          setTwoFAOpen(true);
          return;
        }
      }

      await remove("/transferTable", body);

      enqueueSnackbar(t("deletedTransfer"), { variant: "success" });

      getTransferTable();
      setDeleteModalOpen(false);
      seteditRow(null);
    } catch (err) {
      const errorMsg = err?.message?.msg || err?.message || t("errorTransfer");
      enqueueSnackbar(errorMsg, { variant: "error" });
    }
  };

  const table = useMaterialReactTable({
    columns,
    data,
    localization:
      i18n.language === "ES" ? MRT_Localization_ES : MRT_Localization_EN,
    displayColumnDefOptions: {
      "mrt-row-actions": {
        muiTableHeadCellProps: {
          align: "left",
        },
        size: 50,
      },
    },
    positionActionsColumn: "last",
    enableColumnOrdering: true,
    enableGlobalFilter: false,
    enableDelete: true,
    editDisplayMode: "modal",
    enableEditing: true,
    initialState: {
      density: "compact",
    },
    mrtTheme: () => ({
      baseBackgroundColor: theme.darkTheme
        ? modifiedLightTheme.palette.background.default
        : modifiedDarkTheme.palette.background.default,
    }),
    renderRowActions: ({ row }) => (
      <Box sx={{ display: "flex", gap: "1rem" }}>
        <Tooltip
          arrow
          placement="left"
          title={userPermissions ? t("editButton") : t("noPermissionTooltip")}>
          <span>
            <IconButton
              id="edit-icon-transfer"
              onClick={() => {
                setEditModalOpen(true);
                seteditRow(row);
              }}
              disabled={!userPermissions}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip
          arrow
          placement="right"
          title={
            userPermissions ? t("deleteButton") : t("noPermissionTooltip")
          }>
          <span>
            <IconButton
              id="delete-icon-transfer"
              sx={{ color: "deleteIcon.color" }}
              onClick={() => {
                setDeleteModalOpen(true);
                seteditRow(row);
              }}
              disabled={row.original.is_default || !userPermissions}>
              <Delete />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}>
        <div style={{ marginLeft: "1rem" }}>
          <Typography variant="h4">{t("tittleTransfers")}</Typography>
          <Typography variant="subtitle3">{t("subTittleTransfers")}</Typography>
        </div>
        <Tooltip
          arrow
          placement="right"
          title={!userPermissions ? t("noPermissionTooltip") : ""}>
          <span>
            <Button
              id="new-button-transfer"
              className="mrt-create-new-account-button"
              onClick={() => {
                setEditModalOpen(true);
                setTypeModal("new");
              }}
              disabled={!userPermissions}
              variant="contained">
              {t("addButtonTransfer")}
            </Button>
          </span>
        </Tooltip>
      </div>
    ),
  });

  return (
    <div>
      <ThemeProvider
        theme={theme.darkTheme ? modifiedLightTheme : modifiedDarkTheme}>
        <MaterialReactTable table={table} />
      </ThemeProvider>
      <DeleteRowModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => {
          deleteTransferTable(editRow);
        }}
        title={t("tittleDeleteModalTransfer", {
          inboundName: editRow?.original?.inboundName,
          transferName: clientList.find(
            (x) => x.name === editRow?.original?.transferName,
          )?.display_name,
        })}
      />
      <EditModal
        providersData={providersData}
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setTypeModal(null);
        }}
        data={{
          inboundName: editRow?.original?.inboundName,
          transferName: editRow?.original?.transferName,
          provider: editRow?.original?.provider,
          description: editRow?.original?.description,
        }}
        onSave={(values) => {
          if (typeModal === "new") {
            newTransferTable(values);
            setEditModalOpen(false);
          } else {
            updateTransferTable(editRow, values);
            setEditModalOpen(false);
          }
        }}
        typeModal={typeModal}
        clientList={clientList}
      />
      <TwoFAModal
        open={twoFAOpen}
        onClose={() => {
          setTwoFAOpen(false);
          setPendingAction(null); 
        }}
        onConfirm={handleConfirm2FA}
        onResend={handleResendToken}
      />
    </div>
  );
};

export default TransferTable;